import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { Button } from "react-bootstrap";
import { prizeTable } from "./prizeDistribution";
import { tokens } from "../../theme";

// Example shown in the dialog: 1,000 SC shared by 5 winners.
const EXAMPLE_POOL = 1000;
const EXAMPLE_WINNERS = 5;
const EXAMPLE_STEEPNESS = [
  { value: 0, label: "0 (flat)" },
  { value: 0.5, label: "0.5 (gentle)" },
  { value: 0.75, label: "0.75 (like Evoplay)" },
  { value: 1, label: "1 (default)" },
  { value: 2, label: "2 (very steep)" },
];

const formatSc = (value) => Number(value).toLocaleString("en-US", { maximumFractionDigits: 2 });

/** "?" link under the steepness field that explains what steepness does. */
const SteepnessHelp = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          marginTop: "6px",
          padding: 0,
          border: 0,
          background: "none",
          color: tokens.blue,
          fontSize: "13px",
          cursor: "pointer",
        }}
      >
        <HelpOutlineIcon style={{ fontSize: "18px" }} />
        What is steepness?
      </button>

      <Dialog maxWidth="sm" fullWidth open={open} onClose={() => setOpen(false)}>
        <DialogTitle>What is prize steepness?</DialogTitle>
        <DialogContent dividers>
          <p>
            <b>Steepness decides how much more 1st place gets than everyone else.</b> Think of it as the tilt of a
            slide: flat (0) gives everyone the same amount, steep (a high number) gives 1st place a big share and the
            rest much less.
          </p>

          <p style={{ marginBottom: "6px" }}>
            Example: <b>{formatSc(EXAMPLE_POOL)} SC</b> shared by <b>{EXAMPLE_WINNERS} winners</b>
          </p>
          <Table size="small" className="c_table">
            <TableHead>
              <TableRow>
                <TableCell>Steepness</TableCell>
                {[1, 2, 3, 4, 5].map((rank) => (
                  <TableCell key={rank} align="right">
                    #{rank}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {EXAMPLE_STEEPNESS.map(({ value, label }) => (
                <TableRow key={value}>
                  <TableCell>{label}</TableCell>
                  {prizeTable(EXAMPLE_POOL, EXAMPLE_WINNERS, value).map((row) => (
                    <TableCell key={row.rank} align="right">
                      {formatSc(row.prize)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p style={{ marginTop: "8px", fontSize: "13px", color: tokens.textDim }}>
            The total is always the full pool (minus a few cents of rounding). Steepness only changes how it is
            divided.
          </p>

          <p style={{ marginBottom: "4px" }}>
            <b>Easy way to remember</b>
          </p>
          <ul>
            <li>
              <b>0</b>: everyone gets the same.
            </li>
            <li>
              <b>1</b>: 2nd gets half of 1st, 3rd gets a third, 4th a quarter, and so on.
            </li>
            <li>
              <b>Between 0 and 1</b> (like 0.75): higher ranks still win more, but the gap is smaller.
            </li>
            <li>
              <b>Above 1</b>: the top player takes most of the pool.
            </li>
          </ul>

          <p style={{ marginBottom: "4px" }}>
            <b>Which to choose</b>
          </p>
          <ul>
            <li>Many players should feel rewarded: <b>0.5</b></li>
            <li>Balanced split, like Evoplay: <b>0.75</b></li>
            <li>Clear gap between ranks (2nd gets half of 1st): <b>1</b> (default)</li>
            <li>Almost winner takes all: <b>1.5 to 2</b></li>
          </ul>

          <p style={{ marginBottom: "4px" }}>
            <b>The formula</b>
          </p>
          <pre
            style={{
              background: tokens.surface2,
              border: `1px solid ${tokens.border}`,
              borderRadius: "6px",
              padding: "10px",
              fontSize: "12px",
              whiteSpace: "pre-wrap",
            }}
          >
            {`prize(rank) = pool × rank^(−steepness) ÷ (1^(−steepness) + 2^(−steepness) + … + winners^(−steepness))`}
          </pre>
          <p style={{ fontSize: "13px", color: tokens.textDim }}>
            Prizes are rounded down to the cent. A player with score 0, or ranked below the number of winners, gets
            nothing. The prize table in the form shows the exact split for the values you enter.
          </p>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SteepnessHelp;
